import * as palette from "@govlaunch/palette";
import React from "react";
import apostrophe from "~/lib/utils/apostrophe";
import { IGovernment, ICity } from "~/types/types";
import { NoProducts } from "~/components/profile/EmptyBlocks";
import { FormattedMessage } from "react-intl";

interface INoProductsInStack {
  government: Pick<IGovernment, "_id" | "viewerIsMember"> & {
    city: Pick<ICity, "name">;
    products: any[] | null;
  };
  onAddProduct: () => any;
}

interface ICollectingIllustration {
  height?: string | number;
  width?: string | number;
}

export const NoProductsInStack: React.FunctionComponent<INoProductsInStack> = ({ government, onAddProduct }) => {
  if (!government.viewerIsMember) {
    return (
      <div
        css={{
          backgroundColor: palette.white,
          borderRadius: 5,
          border: `solid 1px ${palette.lightestGray}`,
        }}
        data-intercom-target="Government Stack"
      >
        <div
          css={{
            padding: 15,
            borderBottom: `solid 1px ${palette.lightestGray}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span
            css={{
              textTransform: "uppercase",
              fontSize: 12,
              color: palette.sealBlue,
              letterSpacing: 0.5,
              fontWeight: 500,
            }}
          >
            <FormattedMessage
              defaultMessage="{cityName} Stack ({productsCount})" id="dxKKRK"
              values={{
                cityName: apostrophe(government.city.name),
                productsCount: (government.products || []).length,
              }}
            />
          </span>
        </div>

        <div
          css={{
            padding: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CollectingIllustration width={72} height={75} />

          <div
            css={{
              textAlign: "left",
              marginLeft: 20,
            }}
          >
            <p
              css={{
                fontSize: 21,
                margin: 0,
                color: palette.darkGray,
                fontWeight: "bold",
              }}
            >
              <FormattedMessage
                defaultMessage="{cityName} has not added any products to their Stack" id="4bE2qZ"
                values={{ cityName: government.city.name }}
              />
            </p>

            <p
              css={{
                fontSize: 14,
                margin: 0,
                marginTop: 2.5,
                fontWeight: 400,
                color: palette.mediumGray,
              }}
            >
              <FormattedMessage defaultMessage="Stack is a collection of products the government is currently using." id="6yi5le" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <NoProducts data-intercom-target="Government Stack" onAddProduct={onAddProduct} />;
};

const CollectingIllustration: React.FunctionComponent<ICollectingIllustration> = props => (
  <svg width={104} height={108.5} viewBox="0 0 208 217" {...props}>
    <defs>
      <linearGradient id="prefix__a" x1="0%" y1="50.001%" y2="50.001%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__b" x1="0%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__c" x1="50%" x2="50%" y1="99.994%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__d" x1="0%" y1="49.981%" y2="49.981%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__e" x1="50%" x2="50%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__f" x1="0%" x2="100.006%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__g" x1="0%" y1="49.997%" y2="49.997%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__h" x1="0%" x2="100.031%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__i" x1="0%" y1="49.996%" y2="49.996%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__j" x1="50.007%" x2="50.007%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__k" x1="0%" y1="50.008%" y2="50.008%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__l" x1="50.017%" x2="50.017%" y1="99.899%" y2="-.151%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__m" x1="0%" x2="100.037%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__n" x1=".089%" y1="50.127%" y2="50.127%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__o" x1="0%" x2="100.04%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__p" x1="50.011%" x2="50.011%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__q" x1="0%" y1="49.969%" y2="49.969%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__r" x1="0%" x2="99.987%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__s" x1="0%" x2="99.982%" y1="49.845%" y2="49.845%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__t" x1="0%" y1="49.845%" y2="49.845%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__u" x1="0%" x2="99.982%" y1="50.155%" y2="50.155%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__v" x1="0%" y1="50.155%" y2="50.155%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__w" x1="0%" x2="99.991%" y1="49.994%" y2="49.994%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__x" x1="0%" x2="99.991%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__y" x1="0%" y1="49.875%" y2="49.875%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__z" x1="0%" y1="50.125%" y2="50.125%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__A" x1="50.006%" x2="50.006%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__B" x1="50.003%" x2="50.003%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__C" x1="50.013%" x2="50.013%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__D" x1="49.983%" x2="49.983%" y1="100.203%" y2=".254%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__E" x1="49.983%" x2="49.983%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
      <linearGradient id="prefix__F" x1="50.005%" x2="50.005%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="gray" stopOpacity={0.25} />
        <stop offset="54%" stopColor="gray" stopOpacity={0.12} />
        <stop offset="100%" stopColor="gray" stopOpacity={0.1} />
      </linearGradient>
    </defs>
    <g fill="none" fillRule="evenodd">
      <g opacity={0.7} transform="translate(0 36)">
        <path fill="url(#prefix__a)" d="M171.998 67.553l-68.44-16.52V180.33l68.44-17.956z" />
        <path
          fill="url(#prefix__a)"
          d="M68.545 16.557L.105.037v129.296l68.44-17.957z"
          opacity={0.1}
          transform="translate(103.453 50.996)"
        />
        <path fill="url(#prefix__a)" d="M35.115 67.553l68.443-16.52V180.33l-68.443-17.6z" />
        <path
          fill="url(#prefix__a)"
          d="M.065 16.557L68.508.037v129.296l-68.443-17.6z"
          opacity={0.17}
          transform="translate(35.05 50.996)"
        />
        <path
          fill="url(#prefix__b)"
          d="M103.558 51.033l35.474-29.45 68.44 17.6-35.474 28.37zM0 39.54l35.115 28.013 68.443-16.52-34.759-29.45z"
        />
        <path
          fill="url(#prefix__b)"
          d="M0 18.007l35.115 28.014 68.443-16.52L68.799.051z"
          opacity={0.05}
          transform="translate(0 21.532)"
        />
        <rect
          width={50.8}
          height={46.979}
          x={0.22}
          y={0.108}
          fill="url(#prefix__c)"
          opacity={0.5}
          rx={4.5}
          transform="translate(52.575 35.98)"
        />
        <rect width={49.629} height={45.84} x={53.414} y={36.451} fill="url(#prefix__b)" rx={4.5} />
        <rect width={8.37} height={7.55} x={57.239} y={44.001} fill="url(#prefix__d)" rx={3.775} />
        <rect width={33.272} height={1} x={68.118} y={44.001} fill="url(#prefix__b)" rx={0.5} />
        <rect width={33.272} height={1} x={68.118} y={46.517} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={55.654} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={58.17} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={61.524} fill="url(#prefix__b)" rx={0.5} />
        <rect width={25.945} height={1} x={56.402} y={64.043} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={67.389} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={69.905} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={56.402} y={73.262} fill="url(#prefix__b)" rx={0.5} />
        <rect width={25.945} height={1} x={56.402} y={75.778} fill="url(#prefix__b)" rx={0.5} />
        <rect
          width={50.8}
          height={46.979}
          x={0.254}
          y={0.195}
          fill="url(#prefix__e)"
          opacity={0.5}
          rx={4.5}
          transform="translate(60.206 46.747)"
        />
        <rect width={49.629} height={45.84} x={61.08} y={47.308} fill="url(#prefix__b)" rx={4.5} />
        <rect width={8.37} height={7.55} x={64.904} y={54.858} fill="url(#prefix__d)" rx={3.775} />
        <rect width={33.272} height={1} x={75.787} y={54.858} fill="url(#prefix__b)" rx={0.5} />
        <rect width={33.272} height={1} x={75.787} y={57.374} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={66.508} fill="url(#prefix__f)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={69.027} fill="url(#prefix__f)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={72.381} fill="url(#prefix__f)" rx={0.5} />
        <rect width={25.945} height={1} x={64.068} y={74.897} fill="url(#prefix__b)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={78.246} fill="url(#prefix__f)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={80.761} fill="url(#prefix__f)" rx={0.5} />
        <rect width={44.988} height={1} x={64.068} y={84.119} fill="url(#prefix__f)" rx={0.5} />
        <rect width={25.945} height={1} x={64.068} y={86.634} fill="url(#prefix__b)" rx={0.5} />
        <path
          fill="url(#prefix__e)"
          d="M.116.042h44.016v51.835H.116z"
          opacity={0.5}
          transform="translate(89.886 52.98)"
        />
        <path fill="url(#prefix__g)" d="M91.356 53.858h41.726v49.855H91.356z" />
        <path
          fill="url(#prefix__e)"
          fillRule="nonzero"
          d="M7.929 1.44V.186H3.392v1.252H.2V7.59h10.91V1.44H7.93zM3.957.636H7.35v.797H3.957V.637z"
          opacity={0.5}
          transform="translate(106.563 60.346)"
        />
        <path fill="url(#prefix__b)" d="M107.224 61.992h9.989v5.632h-9.989z" />
        <path
          fill="url(#prefix__b)"
          d="M110.138 60.844v1.462h.52v-1.042h3.12v1.042h.521v-1.462zM97.6 75.73h29.343v1.252H97.6zm0 8.238h29.343v1.252H97.6zm0 2.19h29.343v1.252H97.6zm0-4.38h12.383v1.252H97.6z"
        />
        <path
          fill="url(#prefix__b)"
          d="M.096.184h9.26v1.252H.096z"
          opacity={0.6}
          transform="translate(117.586 81.594)"
        />
        <path fill="url(#prefix__h)" d="M107.9 91.269h9.26v1.252h-9.26z" />
        <path
          fill="url(#prefix__b)"
          d="M35.115 67.553v95.177l68.443 17.6V85.871zm136.883 94.82l-68.44 17.956V85.872l68.44-18.319z"
        />
        <path
          fill="url(#prefix__b)"
          d="M68.545 94.944L.105 112.9V18.444L68.545.124z"
          opacity={0.07}
          transform="translate(103.453 67.429)"
        />
        <path
          fill="url(#prefix__i)"
          d="M.042 7.808v31.247l27.235-7.542V.266z"
          opacity={0.07}
          transform="translate(144.722 122.958)"
        />
        <path fill="url(#prefix__j)" d="M136.18 59.615h20.185v19.438H136.18z" />
        <path fill="url(#prefix__k)" d="M136.496 60.091h19.552v18.566h-19.552z" />
        <path
          fill="url(#prefix__l)"
          fillRule="nonzero"
          d="M6.105 1.017V.071H2.65v.952H.226v4.68h8.302V1.017H6.105zM3.081.411h2.595v.606H3.08V.411z"
          opacity={0.5}
          transform="translate(141.895 62.329)"
        />
        <path
          fill="url(#prefix__m)"
          d="M.011.042h7.598v4.284H.011z"
          opacity={0.6}
          transform="translate(142.46 63.462)"
        />
        <path
          fill="url(#prefix__n)"
          d="M.249.017V1.13h.398V.337h2.375v.793h.395V.017z"
          opacity={0.6}
          transform="translate(144.439 62.612)"
        />
        <path fill="url(#prefix__o)" d="M.164.008h7.044v1H.164z" opacity={0.6} transform="translate(142.743 71.112)" />
        <path fill="url(#prefix__b)" d="M140.256 72.865h12.033v1h-12.033z" />
        <path fill="url(#prefix__p)" d="M.139.195h26.152v21.532H.139z" opacity={0.5} transform="translate(88.19)" />
        <path fill="url(#prefix__b)" d="M88.634.603h25.493V21.37H88.634z" />
        <path fill="url(#prefix__q)" d="M88.634.603h25.493v4.632H88.634z" />
        <path
          fill="url(#prefix__q)"
          d="M.161.037h25.493v4.632H.161z"
          opacity={0.1}
          transform="translate(88.472 .567)"
        />
        <path fill="url(#prefix__r)" d="M90.666 6.916h21.431v2.697H90.666z" />
        <path fill="url(#prefix__s)" d="M90.666 11.65h15.642v1H90.666z" />
        <path fill="url(#prefix__t)" d="M90.666 13.228h10.156v1H90.666zm0 1.578h13.407v1H90.666z" />
        <path fill="url(#prefix__u)" d="M90.666 16.381h15.642v1H90.666z" />
        <path fill="url(#prefix__v)" d="M90.666 17.959h5.229v1h-5.229z" />
        <path
          fill="url(#prefix__e)"
          d="M.147.215h32.415V26.9H.147z"
          opacity={0.5}
          transform="translate(126.631 16.149)"
        />
        <path fill="url(#prefix__w)" d="M127.157 16.868h31.599v25.739h-31.599z" />
        <path fill="url(#prefix__x)" d="M127.157 16.868h31.599v5.74h-31.599z" />
        <path fill="url(#prefix__b)" d="M129.673 24.691h26.562v3.343h-26.562z" />
        <path fill="url(#prefix__y)" d="M129.673 30.558h19.388v1.136h-19.388z" />
        <path fill="url(#prefix__z)" d="M129.673 32.513h12.59v1.136h-12.59z" />
        <path fill="url(#prefix__y)" d="M129.673 34.471h16.618v1.136h-16.618zm0 1.955h19.388v1.136h-19.388z" />
        <path fill="url(#prefix__z)" d="M129.673 38.38h6.484v1.136h-6.484z" />
        <path fill="url(#prefix__b)" d="M131.058 25.827h7.553v1.009h-7.553z" />
        <path
          fill="url(#prefix__b)"
          d="M.02 48.376l38.34 9.698 30.815-25.142V17.49L.735.249z"
          opacity={0.1}
          transform="translate(34.202 68.562)"
        />
        <path
          fill="url(#prefix__b)"
          d="M69.365 48.376l-38.343 9.698L.206 32.932V17.49L68.646.249z"
          opacity={0.1}
          transform="translate(103.17 68.562)"
        />
        <path fill="url(#prefix__b)" d="M35.115 67.553L0 96.647l68.44 17.24 35.118-28.015z" />
        <path fill="url(#prefix__b)" d="M171.998 67.553l35.474 29.094-68.081 17.24-35.833-28.015z" />
        <path
          fill="url(#prefix__b)"
          d="M68.545.125l35.474 29.093-68.082 17.24L.105 18.444z"
          opacity={0.07}
          transform="translate(103.453 67.429)"
        />
      </g>
      <path fill="#535461" d="M172 103.357L104 87v128l68-17.777z" />
      <path fill="#000" d="M172 103.357L104 87v128l68-17.777z" opacity={0.1} />
      <path fill="#535461" d="M36 103.357L104 87v128l-68-17.423z" />
      <path fill="#000" d="M36 101.412L104 85v129l-68-17.482z" opacity={0.17} />
      <path
        fill="#535461"
        d="M104 87.67L139.532 58 207 75.394 171.468 104zM2 75.577L36.589 103 104 86.827 69.765 58z"
      />
      <path fill="#000" d="M2 75.747L37.486 104 105 87.67 69.869 58z" opacity={0.05} />
      <rect
        width={50.484}
        height={45.833}
        x={0.235}
        y={0.067}
        fill="url(#prefix__e)"
        opacity={0.5}
        rx={4.5}
        transform="translate(54 72)"
      />
      <rect width={49} height={45} x={54} y={72} fill="#F5F5F5" rx={4.5} />
      <rect width={8} height={7} x={58} y={80} fill="#005AEE" rx={3.5} />
      <rect width={33} height={1} x={69} y={79} fill="#E0E0E0" rx={0.5} />
      <rect width={33} height={1} x={69} y={82} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={91} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={93} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={97} fill="#E0E0E0" rx={0.5} />
      <rect width={26} height={1} x={57} y={99} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={102} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={105} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={58} y={108} fill="#E0E0E0" rx={0.5} />
      <rect width={26} height={1} x={57} y={111} fill="#E0E0E0" rx={0.5} />
      <rect
        width={50.766}
        height={45.833}
        x={0.205}
        y={0.064}
        fill="url(#prefix__A)"
        opacity={0.5}
        rx={4.5}
        transform="translate(61 83)"
      />
      <rect width={49} height={45} x={62} y={83} fill="#FFF" rx={4.5} />
      <rect width={8} height={7} x={66} y={90} fill="#005AEE" rx={3.5} />
      <rect width={33} height={1} x={77} y={90} fill="#E0E0E0" rx={0.5} />
      <rect width={33} height={1} x={77} y={92} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={102} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={104} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={108} fill="#E0E0E0" rx={0.5} />
      <rect width={26} height={1} x={65} y={110} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={114} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={116} fill="#E0E0E0" rx={0.5} />
      <rect width={44} height={1} x={66} y={119} fill="#E0E0E0" rx={0.5} />
      <rect width={26} height={1} x={65} y={121} fill="#E0E0E0" rx={0.5} />
      <path fill="url(#prefix__B)" d="M.097.146h43.827v51.826H.097z" opacity={0.5} transform="translate(91 88)" />
      <path fill="#FFF" d="M92 89h41v50H92z" />
      <path
        fill="url(#prefix__C)"
        fillRule="nonzero"
        d="M7.683 1.34V.202H3.184V1.34H.037v5.582H10.83V1.34H7.683zM3.734.617H7.12v.723H3.734V.617z"
        opacity={0.5}
        transform="translate(107 96)"
      />
      <path fill="#005AEE" d="M108 97h10v6h-10z" />
      <path fill="#005AEE" d="M111 96v2h.499v-1.429h3V98H115v-2z" />
      <path fill="#E0E0E0" d="M98 111h29v1H98zm0 8h29v1H98zm0 2h29v1H98zm0-4h12v1H98z" />
      <path fill="#3AD29F" d="M118 117h9v1h-9z" opacity={0.6} />
      <path fill="#005AEE" d="M109 126h9v1h-9z" />
      <path fill="#535461" d="M36 102v94.521L104.081 214l.919-93.809z" />
      <path fill="#535461" d="M172 196.166L104 214v-93.809L172 102z" />
      <path fill="#000" d="M172 196.166L104 214v-93.809L172 102z" opacity={0.07} />
      <path fill="#000" d="M145 165.585V197l27-7.582V158z" opacity={0.07} />
      <path fill="url(#prefix__e)" d="M137 95h20v19h-20z" />
      <path fill="#F5F5F5" d="M136 96h20v18h-20z" />
      <path
        fill="url(#prefix__D)"
        fillRule="nonzero"
        d="M5.541 1.089V.075H2.307v1.002H.043v4.914h7.762V1.089H5.541zM2.712.453h2.427v.636H2.712V.453z"
        opacity={0.5}
        transform="translate(143 98)"
      />
      <path
        fill="#005AEE"
        d="M143 99h7v4h-7zm2-1v1h.376v-.715h2.251V99H148v-1zm-1.755 8.336h6.653v1.332h-6.653z"
        opacity={0.6}
      />
      <path fill="#BDBDBD" d="M140 108h12v1h-12z" />
      <path fill="url(#prefix__e)" d="M117 0h20v19h-20z" />
      <path fill="#F5F5F5" d="M117 1h20v18h-20z" />
      <path
        fill="url(#prefix__E)"
        fillRule="nonzero"
        d="M5.576 1.157V.206H2.341v.951H.077v4.68H7.84v-4.68H5.576zM2.746.551h2.427v.606H2.747V.551z"
        opacity={0.5}
        transform="translate(123 3)"
      />
      <path
        fill="#005AEE"
        d="M124 4h7v4h-7zm2-1v1h.376v-.715h2.251V4H129V3zm-1.989 9.065h6.919v1h-6.919z"
        opacity={0.6}
      />
      <path fill="#BDBDBD" d="M121 13h12v1h-12z" />
      <path fill="url(#prefix__F)" d="M.131.185h25.645v21.812H.131z" opacity={0.5} transform="translate(89 36)" />
      <path fill="#F2F2F2" d="M89 36h25v21H89z" />
      <path fill="#005AEE" d="M89 36h25v5H89z" />
      <path fill="#000" d="M89 36h25v5H89z" opacity={0.1} />
      <path fill="#E0E0E0" d="M91 43h21v3H91zm0 5h15v1H91zm0 1h10v1H91zm0 2h13v1H91zm0 1h15v1H91zm0 2h5v1h-5z" />
      <path fill="url(#prefix__e)" d="M.095.061h32.627v25.831H.095z" opacity={0.5} transform="translate(127 52)" />
      <path fill="#FFF" d="M128 53h31v25h-31z" />
      <path fill="#005AEE" d="M128 53h31v6h-31z" />
      <path fill="#F5F5F5" d="M130 60h26v3h-26z" />
      <path fill="#E0E0E0" d="M130 66h19v1h-19zm0 2h13v1h-13zm1 2h16v1h-16zm-1 2h19v1h-19zm1 2h6v1h-6z" />
      <path fill="#69F0AE" d="M132 61h7v1h-7z" />
      <path
        fill="#000"
        d="M35 151.442L73.253 161 104 136.217v-15.223L35.714 104zm138 0L134.747 161 104 136.217v-15.223L172.286 104z"
        opacity={0.1}
      />
      <path
        fill="#535461"
        d="M36.589 103L2 131.883 69.411 149 104 121.187zm136.249 0L208 131.883 140.518 149 105 121.187z"
      />
      <path fill="#000" d="M172.838 103L208 131.883 140.518 149 105 121.187z" opacity={0.07} />
    </g>
  </svg>
);
